import { Injectable } from '@angular/core';
import { CustomBreadcrumb, CustomFacetContentType, CustomProductSearchPage, RangeFacet } from '@catalog/const';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ActiveFacet } from '@search/const';
import { SearchActions } from '@search/data-access';
import { ProductActions } from '@spartacus/core';
import { Observable, from } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

type FacetActions =
  | SearchActions.SetFacetRangeValue
  | SearchActions.ClearAllFacets
  | SearchActions.SetFacetValue
  | SearchActions.ToggleFacet;

@Injectable()
export class ActiveFacetsEffects {
  restoreActiveFacetsAfterLoadProducts$: Observable<FacetActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.SEARCH_PRODUCTS_SUCCESS),
      map(({ payload }) => payload),
      map(({ breadcrumbs }: CustomProductSearchPage) => {
        const actions: FacetActions[] = [new SearchActions.ClearAllFacets()];
        const rangeFacets = breadcrumbs.filter(({ contentType }) => contentType === CustomFacetContentType.Range);
        const checkboxFacets = breadcrumbs.filter(({ contentType }) => contentType === CustomFacetContentType.Checkbox);

        checkboxFacets.map(this.mapBreadcrumbsToFacets).forEach((facet: ActiveFacet) => {
          actions.push(
            new SearchActions.ToggleFacet({
              facet,
              value: facet.value,
              force: true,
            }),
          );
        });

        const groupedRangeFacets: Record<string, CustomBreadcrumb[]> = rangeFacets.reduce(
          (acc: Record<string, CustomBreadcrumb[]>, cur) => {
            acc[cur.facetCode] = [...(acc[cur.facetCode] || []), cur];

            return acc;
          },
          {},
        );

        Object.values(groupedRangeFacets).forEach((rangeBreadcrumbs: CustomBreadcrumb[]) => {
          const { facetCode, facetName } = rangeBreadcrumbs[0];
          const values = rangeBreadcrumbs.map(({ facetValueCode }) => Number(facetValueCode));
          const minValue = Math.min(...values);
          const maxValue = Math.max(...values);
          const facet: RangeFacet = {
            contentType: CustomFacetContentType.Range,
            name: facetName,
            code: facetCode,
            minValue,
            maxValue,
          };

          actions.push(new SearchActions.SetFacetRangeValue({ facet, value: values }));
        });

        return actions;
      }),
      mergeMap((actions: FacetActions[]) => from(actions)),
    ),
  );

  constructor(private actions$: Actions) {}

  private mapBreadcrumbsToFacets({
    contentType,
    facetName: name,
    facetCode: code,
    facetValueCode: valueCode,
    facetValueName: valueName,
  }: CustomBreadcrumb): ActiveFacet {
    return {
      contentType,
      code,
      name,
      value: { name: valueName, code: valueCode },
    };
  }
}
